/**
 * useFAAFacilityMap — loads FAA UAS Facility Map polygons for the current
 * camera viewport onto a Cesium viewer.
 *
 * Instead of fetching all ~378K polygons (which crashes the browser), this
 * hook listens to camera changes, computes the visible bounding box, and
 * queries only the features within that viewport via ArcGIS spatial queries.
 *
 * Typical viewport fetch returns 50–600 features — well within browser limits.
 */

import { useEffect, useRef, useCallback } from 'react';
import {
  Viewer as CesiumViewer,
  GeoJsonDataSource,
  ColorMaterialProperty,
  ConstantProperty,
  ConstantPositionProperty,
  Math as CesiumMath,
  Cartesian3,
  Cartographic,
  Color,
  JulianDate,
  LabelGraphics as CesiumLabelGraphics,
  LabelStyle,
  VerticalOrigin,
  HorizontalOrigin,
  NearFarScalar,
} from 'cesium';
import {
  fetchFAAFacilitiesInBBox,
  CEILING_COLORS,
  DEFAULT_FACILITY_COLOR,
  FAAFeatureCollection,
} from '../services/faaFacilityMap';

const FEET_TO_METERS = 0.3048;

/** Don't fetch facilities when camera is higher than this (meters). */
const MAX_CAMERA_HEIGHT = 500_000; // ~500 km

/** Debounce delay after camera stops moving before fetching (ms). */
const DEBOUNCE_MS = 300;

/**
 * Style every polygon entity in a GeoJsonDataSource based on its CEILING value,
 * and add a label at the polygon centroid showing the altitude.
 */
function styleEntities(ds: GeoJsonDataSource) {
  const entities = ds.entities.values;
  const now = JulianDate.now();

  for (const entity of entities) {
    const ceiling = entity.properties?.CEILING?.getValue() as
      | number
      | undefined;
    const ceilingValue = ceiling ?? 0;
    const baseColor = CEILING_COLORS[ceilingValue] ?? DEFAULT_FACILITY_COLOR;

    if (entity.polygon) {
      // ── Polygon styling ────────────────────────────────────────────────
      entity.polygon.material = new ColorMaterialProperty(
        baseColor.withAlpha(0.35)
      );
      entity.polygon.outline = new ConstantProperty(true);
      entity.polygon.outlineColor = new ConstantProperty(baseColor);
      entity.polygon.outlineWidth = new ConstantProperty(1);
      entity.polygon.extrudedHeight = new ConstantProperty(
        ceilingValue * FEET_TO_METERS
      );
      entity.polygon.height = new ConstantProperty(0);

      // ── Compute centroid for label placement ───────────────────────────
      try {
        const hierarchy = entity.polygon.hierarchy?.getValue(now);
        if (hierarchy && hierarchy.positions && hierarchy.positions.length > 0) {
          const positions: Cartesian3[] = hierarchy.positions;
          let cx = 0, cy = 0, cz = 0;
          for (const p of positions) {
            cx += p.x;
            cy += p.y;
            cz += p.z;
          }
          const n = positions.length;
          const centroid = new Cartesian3(cx / n, cy / n, cz / n);

          // Lift label to half the extruded height so it sits in the middle
          const carto = Cartographic.fromCartesian(centroid);
          carto.height = (ceilingValue * FEET_TO_METERS) / 2;
          const labelPos = Cartesian3.fromRadians(
            carto.longitude,
            carto.latitude,
            carto.height
          );

          entity.position = new ConstantPositionProperty(labelPos);

          // ── Altitude label ───────────────────────────────────────────
          entity.label = new CesiumLabelGraphics({
            text: `${ceilingValue} ft`,
            font: 'bold 13px "DM Sans", sans-serif',
            fillColor: Color.WHITE,
            style: LabelStyle.FILL,
            showBackground: false,
            verticalOrigin: VerticalOrigin.CENTER,
            horizontalOrigin: HorizontalOrigin.CENTER,
            scaleByDistance: new NearFarScalar(500, 1.2, 50_000, 0.5),
            translucencyByDistance: new NearFarScalar(1_000, 1.0, 80_000, 0.0),
          });
        }
      } catch {
        // Skip label if centroid computation fails
      }
    }
  }
  return entities.length;
}

export function useFAAFacilityMap(
  viewer: CesiumViewer | null,
  visible: boolean
) {
  const dataSourceRef = useRef<GeoJsonDataSource | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const viewerRef = useRef<CesiumViewer | null>(null);

  // ── Fetch & render for the current viewport ──────────────────────────────

  const loadForViewport = useCallback(async (v: CesiumViewer) => {
    if (v.isDestroyed()) return;

    // Compute visible rectangle
    const rect = v.camera.computeViewRectangle();
    if (!rect) {
      console.log('[FAA Facility Map] No view rectangle — scene not ready yet');
      return;
    }

    const height = v.camera.positionCartographic.height;
    console.log('[FAA Facility Map] Camera height:', Math.round(height), 'm, max:', MAX_CAMERA_HEIGHT, 'm');

    // Too high — too many features; clear and bail
    if (height > MAX_CAMERA_HEIGHT) {
      console.log('[FAA Facility Map] Camera too high — skipping fetch (zoom in to see facilities)');
      if (dataSourceRef.current) {
        dataSourceRef.current.entities.removeAll();
      }
      return;
    }

    const west = CesiumMath.toDegrees(rect.west);
    const south = CesiumMath.toDegrees(rect.south);
    const east = CesiumMath.toDegrees(rect.east);
    const north = CesiumMath.toDegrees(rect.north);

    // Abort any in-flight request
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const geojson: FAAFeatureCollection = await fetchFAAFacilitiesInBBox(
        west,
        south,
        east,
        north,
        controller.signal
      );

      if (controller.signal.aborted || v.isDestroyed()) return;

      // Ensure datasource exists
      let ds = dataSourceRef.current;
      if (!ds) {
        ds = new GeoJsonDataSource('faa-facility-map');
        dataSourceRef.current = ds;
        if (!v.isDestroyed()) {
          await v.dataSources.add(ds);
        }
      }

      if (v.isDestroyed()) return;

      // Load new data (replaces existing entities)
      await ds.load(geojson, { clampToGround: false });

      if (v.isDestroyed()) return;

      const count = styleEntities(ds);
      ds.show = visible;

      console.log(
        `[FAA Facility Map] Loaded ${count} facility polygons for viewport`
      );
    } catch (err: unknown) {
      // Ignore aborted fetches
      if (err instanceof DOMException && err.name === 'AbortError') return;
      console.error('[FAA Facility Map] Failed to load:', err);
    }
  }, [visible]);

  // ── Camera change listener ───────────────────────────────────────────────

  useEffect(() => {
    console.log('[FAA Facility Map] Effect fired — viewer:', viewer ? 'present' : 'null', 'visible:', visible);
    if (!viewer || viewer.isDestroyed()) return;

    // Track viewer so cleanup can reference the right instance
    viewerRef.current = viewer;

    // Create datasource on first attach
    if (!dataSourceRef.current) {
      const ds = new GeoJsonDataSource('faa-facility-map');
      dataSourceRef.current = ds;
      ds.show = visible;
      viewer.dataSources.add(ds);
    }

    const onCameraChange = () => {
      // Debounce: clear previous timer, start new one
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        if (viewer && !viewer.isDestroyed() && visible) {
          loadForViewport(viewer);
        }
      }, DEBOUNCE_MS);
    };

    viewer.camera.changed.addEventListener(onCameraChange);

    // Initial load — wait briefly for the scene to render its first frame
    // so computeViewRectangle() returns a valid rectangle.
    let initialTimer: ReturnType<typeof setTimeout> | null = null;
    if (visible) {
      initialTimer = setTimeout(() => {
        if (!viewer.isDestroyed() && visible) {
          loadForViewport(viewer);
        }
      }, 500);
    }

    return () => {
      // Clear initial load timer
      if (initialTimer) {
        clearTimeout(initialTimer);
      }
      // Remove camera listener
      if (!viewer.isDestroyed()) {
        viewer.camera.changed.removeEventListener(onCameraChange);
      }
      // Clear debounce timer
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      // Abort in-flight fetch
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };
  }, [viewer, visible, loadForViewport]);

  // ── Toggle visibility ────────────────────────────────────────────────────

  useEffect(() => {
    if (dataSourceRef.current) {
      dataSourceRef.current.show = visible;
    }
  }, [visible]);

  // ── Cleanup on unmount ───────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (dataSourceRef.current) {
        try {
          const v = viewerRef.current;
          if (v && !v.isDestroyed()) {
            v.dataSources.remove(dataSourceRef.current, true);
          }
        } catch {
          // viewer may already be destroyed
        }
        dataSourceRef.current = null;
        viewerRef.current = null;
      }
    };
  }, []);
}
